import { useEffect, useState } from 'react';
import { Download, Share2, Zap, Copy, Check, ArrowRight, X, HelpCircle, Activity, Info, Award } from 'lucide-react';
import { useStore } from '../store';
import { Radar, type RadarAxis } from '../components/Radar';
import { FullTranscript } from '../components/FullTranscript';
import { bandByKey } from '../theme/bands';
import { CRITERION_LABELS, type CriterionView, type CustomDimensionView, type FourDKey, type ReportView } from '../types';
import { InfoTip } from '../components/ui';
import { CRITERION_DEF, FOURD_DEF, VELOCITY_DEF } from '../criteria-copy';
import { sampleReport, sampleCohortRadar } from '../sample';
import { isSampleMode } from '../api/client';
import { currentUser } from '../auth/cognito';
import { formatDate } from '../theme/bands';

/**
 * The live report once an assessment completes. The Jordan Mercer sample is only used in
 * sample/demo mode — a signed-in user with no report gets an empty state, never the demo
 * persona's report.
 */
function useReport(): ReportView | null {
  const live = useStore((s) => s.report);
  const showSample = useStore((s) => s.showSample);
  // An explicit "see a sample report" click shows the fixture even in a live deployment, where it
  // is otherwise hidden. Sample/demo mode still falls back to it when there's no live report.
  if (showSample) return sampleReport;
  return live ?? (isSampleMode ? sampleReport : null);
}

const POLICY_LABEL: Record<ReportView['policyFit'], string> = {
  compliant: 'Compliant',
  at_risk: 'At-risk',
  non_compliant: 'Non-compliant',
};
// Band-swatch colour for the policy-fit summary cell.
const POLICY_SWATCH: Record<ReportView['policyFit'], string> = {
  compliant: '#00D700',
  at_risk: '#C2FF01',
  non_compliant: '#E5142B',
};

// Band chip background/ink (matches BandChip / the prototype's inline pills).
const bandPill = (band: CriterionView['band']['band']) => {
  const d = bandByKey(band);
  return { background: d.fill, color: d.text };
};

// Severity chip background/ink (matches the prototype register).
const SEV: Record<'high' | 'medium' | 'low', { bg: string; ink: string; label: string }> = {
  high: { bg: '#E5142B', ink: '#fff', label: 'High' },
  medium: { bg: '#C2FF01', ink: '#000', label: 'Medium' },
  low: { bg: '#C8C8CC', ink: '#000', label: 'Low' },
};

// 4D status chip (background/ink/label).
const FOURD: Record<'strong' | 'in_place' | 'emerging', { bg: string; ink: string; label: string }> = {
  strong: { bg: '#E8F9E8', ink: '#0A7A0A', label: 'Strong' },
  in_place: { bg: '#EAEDFF', ink: '#2438E6', label: 'In place' },
  emerging: { bg: '#F0F0F2', ink: '#75757F', label: 'Emerging' },
};

// Static descriptors for the 4D dimensions (not carried in the band-only payload).
const FOURD_DESC: Record<FourDKey, string> = {
  description: 'You brief AI clearly, with the context it needs.',
  delegation: 'You hand off whole tasks, and know which to keep.',
  discernment: 'You catch most errors; review speed can improve.',
  diligence: 'Versioning and reuse of good prompts is still ad-hoc.',
};
const FOURD_LABEL: Record<FourDKey, string> = {
  description: 'Description',
  delegation: 'Delegation',
  discernment: 'Discernment',
  diligence: 'Diligence',
};

// Static practice options (post-drill choices) — not in the data model.
const PRACTICE_OPTIONS: [string, string][] = [
  ['A', 'Go deeper on this skill'],
  ['B', 'Try a harder scenario'],
  ['C', 'Apply it to a real task on my plate'],
  ['D', 'See a worked example'],
  ['E', 'Move to the next drill'],
];

// Derived (the band-only payload carries no prose summary): highest/lowest-band criteria.
const strongest = (r: ReportView): CriterionView =>
  [...r.criteria].sort((a, b) => bandByKey(b.band.band).level - bandByKey(a.band.band).level)[0];
const opportunity = (r: ReportView): CriterionView =>
  [...r.criteria].sort((a, b) => bandByKey(a.band.band).level - bandByKey(b.band.band).level)[0];

function summarise(r: ReportView): string {
  if (!r.criteria.length) return `Overall you're assessed at ${r.overallReadiness.label}.`;
  const s = CRITERION_LABELS[strongest(r).criterion];
  const o = CRITERION_LABELS[opportunity(r).criterion];
  return `Overall you're assessed at ${r.overallReadiness.label}. Your strongest area is ${s}; the biggest opportunity is ${o}. ${r.primaryBottleneck?.reframe ?? ''}`.trim();
}

const sectionStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #E2E2E6',
  borderRadius: 12,
  padding: '30px 32px',
};
const eyebrowStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  color: '#75757F',
};

function NoReportYet({ onStart }: { onStart: () => void }) {
  return (
    <div style={{ flex: 1, background: '#F4F4F6' }}>
      <div style={{ maxWidth: 620, margin: '0 auto', padding: '72px 32px' }}>
        <div style={{ ...sectionStyle, textAlign: 'center', padding: '40px 36px' }}>
          <div style={{ ...eyebrowStyle, marginBottom: 12 }}>AI Fluency Report</div>
          <h1 style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.02em', margin: '0 0 10px', color: '#000' }}>
            Your report isn’t ready yet
          </h1>
          <p style={{ fontSize: 15, lineHeight: 1.55, color: '#333544', margin: '0 0 22px' }}>
            Complete an assessment with Avery and your personalised report — bands, evidence and a
            90-day roadmap — will appear here.
          </p>
          <button
            onClick={onStart}
            style={{ background: '#00D700', color: '#000', border: '2px solid #00D700', fontSize: 15, fontWeight: 700, padding: '13px 24px', borderRadius: 6, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 9 }}
          >
            Start assessment <ArrowRight size={17} />
          </button>
        </div>
      </div>
    </div>
  );
}

export function Report() {
  const r = useReport();
  const [shareOpen, setShareOpen] = useState(false);
  const [copyStatus, setCopyStatus] = useState('');
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const drawer = useStore((s) => s.drawer);
  const setDrawer = useStore((s) => s.setDrawer);
  const revealCohort = useStore((s) => s.revealCohort);
  const toggleCohort = useStore((s) => s.toggleCohort);
  const openAssessment = useStore((s) => s.openAssessment);
  const showSample = useStore((s) => s.showSample);

  // Live cohort averages (k-anonymised by the API); the sample map only in demo mode.
  const liveCohort = useStore((s) => s.cohort);
  const cohortMap = liveCohort ?? (isSampleMode || showSample ? sampleCohortRadar : null);

  // No report yet (signed in, assessment not completed) — never show the demo report.
  if (!r) return <NoReportYet onStart={openAssessment} />;

  const managerSummary = [
    showSample || isSampleMode ? 'FluentAI capability summary (sample report)' : 'FluentAI capability summary',
    r.name ? `Participant: ${r.name}` : '',
    r.completedAt ? `Assessment date: ${formatDate(r.completedAt)}` : '',
    `Overall readiness: ${r.overallReadiness.label} (${r.confidence} confidence)`,
    ...r.criteria.map((c) => `${CRITERION_LABELS[c.criterion]}: ${c.band.label} (${c.confidence} confidence)`),
    'These are automated assessment results, not an independent validation of capability or policy compliance.',
    'Conversation transcript and evidence quotes are not included.',
  ].filter(Boolean).join('\n');
  const copyManagerSummary = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(managerSummary);
      setCopyStatus('Summary copied. Paste it into your message to your manager.');
    } catch {
      setCopyStatus('Could not copy automatically. Select and copy the summary below.');
    }
  };

  // Historical (pre-platform) imports carry only top-line outcomes — no per-criterion bands,
  // 4D breakdown, workflow findings, roadmap or practice pack. Render a "summary only" view
  // rather than empty section shells.
  const legacy = !!r.imported;

  const axes: RadarAxis[] = r.criteria.map((c) => ({
    criterion: c.criterion,
    band: c.band.band,
    level: bandByKey(c.band.band).level,
  }));
  const cohort =
    revealCohort && cohortMap
      ? r.criteria.map((c) => bandByKey((cohortMap[c.criterion] ?? 'developing') as never).level)
      : undefined;

  return (
    <div style={{ flex: 1, background: '#F4F4F6' }}>
      {/* Sample-preview banner — shown when the user explicitly opened the sample so the example
          data (a fictional persona) is never mistaken for their own report. */}
      {showSample && (
        <div
          className="print:hidden"
          style={{ background: '#111', color: '#fff', borderBottom: '1px solid #26262E' }}
        >
          <div
            style={{ maxWidth: 980, margin: '0 auto', padding: '10px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600 }}>
              <Info size={15} strokeWidth={2.2} color="#00D700" />
              Sample report — example data to show what you’ll get. It isn’t your assessment.
            </span>
            <button
              onClick={openAssessment}
              style={{ flex: 'none', fontSize: 13, fontWeight: 700, color: '#000', background: '#00D700', border: 'none', borderRadius: 6, padding: '7px 14px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              Start your assessment <ArrowRight size={14} strokeWidth={2.4} />
            </button>
          </div>
        </div>
      )}
      {/* report header — dark */}
      <div style={{ background: '#000', color: '#fff' }}>
        <div style={{ maxWidth: 980, margin: '0 auto', padding: '34px 32px 30px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24, flexWrap: 'wrap' }}>
            <div>
              <div style={{ ...eyebrowStyle, color: '#00D700', marginBottom: 14 }}>AI Fluency Report</div>
              <h1 style={{ fontSize: 38, lineHeight: 1.05, letterSpacing: '-0.03em', fontWeight: 700, margin: 0 }}>
                {r.name ?? currentUser()?.name ?? currentUser()?.email ?? 'Your report'}
              </h1>
              <div style={{ fontSize: 15, color: '#ADAEB4', marginTop: 8 }}>
                {r.roleLine ?? `Assessed ${formatDate(r.completedAt) || 'recently'}`}
              </div>
            </div>
            <div className="print:hidden" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                onClick={() => window.print()}
                style={{ background: '#00D700', color: '#000', border: '2px solid #00D700', fontSize: 14, fontWeight: 700, padding: '11px 18px', borderRadius: 6, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, transition: 'all .12s' }}
              >
                <Download size={16} /> Download report
              </button>
              <button
                onClick={() => { setShareOpen(!shareOpen); setCopyStatus(''); }}
                aria-expanded={shareOpen}
                aria-controls="manager-share"
                style={{ background: 'transparent', color: '#fff', border: '1.5px solid #3a3a44', fontSize: 14, fontWeight: 600, padding: '11px 18px', borderRadius: 6, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, transition: 'all .12s' }}
              >
                <Share2 size={16} /> Share with manager
              </button>
              <button
                onClick={() => setTranscriptOpen(!transcriptOpen)}
                aria-expanded={transcriptOpen}
                aria-controls="full-transcript"
                style={{ background: 'transparent', color: '#fff', border: '1.5px solid #3a3a44', fontSize: 14, fontWeight: 600, padding: '11px 18px', borderRadius: 6, cursor: 'pointer' }}
              >Full Transcript</button>
            </div>
          </div>

          {shareOpen && (
            <section id="manager-share" aria-label="Share capability summary" className="print:hidden" style={{ marginTop: 20 }}>
              <p>Review this summary, then copy it into email or Teams. Nothing is sent automatically and no report access is granted.</p>
              <textarea aria-label="Manager summary" readOnly value={managerSummary} rows={10} style={{ width: '100%', boxSizing: 'border-box', padding: 12, color: '#111', background: '#fff', borderRadius: 6 }} />
              <button onClick={copyManagerSummary} style={{ padding: '10px 16px', marginTop: 8, background: '#00D700', color: '#000', borderRadius: 6, cursor: 'pointer' }}>Copy summary</button>
              <p role="status">{copyStatus}</p>
            </section>
          )}

          {transcriptOpen && <FullTranscript key={`${r.assessmentId}-${showSample}`} assessmentId={r.assessmentId} sample={isSampleMode || showSample} />}

          {/* summary band row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 1, background: '#26262E', border: '1px solid #26262E', borderRadius: 8, overflow: 'hidden', marginTop: 26 }}>
            <SummaryCell label="Overall readiness">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 11, height: 11, borderRadius: 3, background: bandByKey(r.overallReadiness.band).fill }} />
                <span style={{ fontSize: 19, fontWeight: 700 }}>{r.overallReadiness.label}</span>
              </span>
              {r.progression && <ProgressionChip p={r.progression} />}
            </SummaryCell>
            <SummaryCell label="Confidence">
              <span style={{ fontSize: 19, fontWeight: 700, textTransform: 'capitalize', color: r.confidence === 'high' ? '#00D700' : '#fff' }}>{r.confidence}</span>
            </SummaryCell>
            <SummaryCell label="Fluency velocity">
              {legacy ? (
                <span style={{ fontSize: 19, fontWeight: 700, color: '#75757F' }}>—</span>
              ) : (
                <span style={{ fontSize: 19, fontWeight: 700, textTransform: 'capitalize' }}>{r.velocity}</span>
              )}
            </SummaryCell>
            <SummaryCell label="Policy fit">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 11, height: 11, borderRadius: 3, background: POLICY_SWATCH[r.policyFit] }} />
                <span style={{ fontSize: 19, fontWeight: 700 }}>{POLICY_LABEL[r.policyFit]}</span>
              </span>
            </SummaryCell>
          </div>
        </div>
      </div>

      <div className="fui-report" style={{ maxWidth: 980, margin: '0 auto', padding: '32px 32px 80px', display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Historical import notice */}
        {legacy && (
          <div className="animate-fadeUp" style={{ display: 'flex', alignItems: 'flex-start', gap: 12, background: '#F4F4F6', border: '1px solid #E2E2E6', borderRadius: 10, padding: '14px 18px' }}>
            <Info size={18} style={{ flex: 'none', color: '#75757F', marginTop: 1 }} />
            <div style={{ fontSize: 13.5, lineHeight: 1.5, color: '#333544' }}>
              <strong style={{ color: '#000' }}>Imported summary.</strong> This assessment was completed before the current platform, so only the headline outcomes were captured — overall readiness, confidence, policy fit and your leverage bottlenecks. The detailed breakdowns (capability profile, 4D readiness, workflow findings and roadmap) weren’t recorded and aren’t shown.
            </div>
          </div>
        )}

        {/* 1. Assessment summary */}
        <section className="animate-fadeUp" style={sectionStyle}>
          <div style={{ ...eyebrowStyle, marginBottom: 12 }}>Assessment summary</div>
          <p style={{ fontSize: 18, lineHeight: 1.6, color: '#000', margin: 0, fontWeight: 500 }}>{summarise(r)}</p>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 22, paddingTop: 20, borderTop: '1px solid #EFEFF2' }}>
            <SummaryStat label="Strongest criterion" value={r.criteria.length ? CRITERION_LABELS[strongest(r).criterion] : '—'} />
            <SummaryStat label="Biggest opportunity" value={r.criteria.length ? CRITERION_LABELS[opportunity(r).criterion] : '—'} />
            <SummaryStat label="Time invested" value={r.durationMins ? `${r.durationMins} minutes` : '—'} />
          </div>
        </section>

        {/* 2. Capability profile — per-criterion data not captured for imports */}
        {!legacy && (
        <section className="animate-fadeUp" style={{ ...sectionStyle, animationDelay: '60ms' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
            <div>
              <div style={{ ...eyebrowStyle, marginBottom: 6 }}>Capability profile</div>
              <h2 style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', margin: 0 }}>Five criteria, strengths first</h2>
            </div>
            {cohortMap ? (
              <button
                onClick={toggleCohort}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 700, padding: '8px 14px', borderRadius: 6, cursor: 'pointer', transition: 'all .12s',
                  ...(revealCohort
                    ? { background: '#00D700', color: '#000', border: '1.5px solid #00D700' }
                    : { background: 'transparent', color: '#333544', border: '1.5px solid #E2E2E6' }),
                }}
              >
                <Activity size={14} /> Compare to cohort
              </button>
            ) : (
              <span style={{ fontSize: 12, color: '#75757F' }}>Cohort comparison needs 5+ completed in your campaign</span>
            )}
          </div>
          <div className="fui-capgrid">
            <div className="fui-capradar">
              <Radar axes={axes} cohort={cohort} compact />
              {revealCohort && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: 18, marginTop: 6 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12, color: '#333544', fontWeight: 600 }}>
                    <span style={{ width: 16, height: 3, background: '#00D700', borderRadius: 2 }} />You
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12, color: '#75757F', fontWeight: 600 }}>
                    <span style={{ width: 16, height: 0, borderTop: '2px dashed #ADAEB4' }} />Cohort average
                  </span>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {r.criteria.map((c, i) => (
                <CriterionRow key={c.criterion} c={c} last={i === r.criteria.length - 1} onWhy={() => setDrawer(c.criterion)} />
              ))}
            </div>
          </div>
        </section>
        )}

        {/* 3 + 4 + 5: 4D readiness, overall readiness, velocity. Imports keep only the
            overall-readiness card (full width) — no 4D breakdown, no measured velocity. */}
        {legacy ? (
          <section className="animate-fadeUp" style={{ ...sectionStyle, display: 'flex', flexDirection: 'column' }}>
            <div style={{ ...eyebrowStyle, marginBottom: 6 }}>Overall readiness</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '6px 0 4px' }}>
              <span style={{ width: 14, height: 14, borderRadius: 4, background: bandByKey(r.overallReadiness.band).fill }} />
              <span style={{ fontSize: 28, fontWeight: 700, letterSpacing: '-0.02em' }}>{r.overallReadiness.label}</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#00A800', background: '#E8F9E8', padding: '3px 9px', borderRadius: 999, marginLeft: 'auto', textTransform: 'capitalize' }}>{r.confidence} confidence</span>
            </div>
          </section>
        ) : (
        <div className="fui-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          {/* 4D readiness */}
          <section className="animate-fadeUp" style={{ ...sectionStyle, animationDelay: '120ms' }}>
            <div style={{ ...eyebrowStyle, marginBottom: 6 }}>4D readiness</div>
            <h2 style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', margin: '0 0 20px' }}>How you work with AI</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {r.fourD.map((d) => {
                const s = FOURD[d.status];
                return (
                  <div key={d.dimension} style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                    <div style={{ flex: 'none', width: 118, fontSize: 14, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}>
                      {FOURD_LABEL[d.dimension]}
                      <InfoTip label={FOURD_LABEL[d.dimension]} text={FOURD_DEF[d.dimension]} />
                    </div>
                    <div style={{ flex: 'none' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, background: s.bg, color: s.ink, padding: '4px 10px', borderRadius: 999 }}>{s.label}</span>
                    </div>
                    <div style={{ fontSize: 13, lineHeight: 1.45, color: '#75757F' }}>{FOURD_DESC[d.dimension]}</div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Overall readiness + velocity */}
          <section className="animate-fadeUp" style={{ ...sectionStyle, animationDelay: '160ms', display: 'flex', flexDirection: 'column' }}>
            <div style={{ ...eyebrowStyle, marginBottom: 6 }}>Overall readiness</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '6px 0 4px' }}>
              <span style={{ width: 14, height: 14, borderRadius: 4, background: bandByKey(r.overallReadiness.band).fill }} />
              <span style={{ fontSize: 28, fontWeight: 700, letterSpacing: '-0.02em' }}>{r.overallReadiness.label}</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#00A800', background: '#E8F9E8', padding: '3px 9px', borderRadius: 999, marginLeft: 'auto', textTransform: 'capitalize' }}>{r.confidence} confidence</span>
            </div>
            <p style={{ fontSize: 14, lineHeight: 1.55, color: '#75757F', margin: '8px 0 0' }}>
              You're consistent and confident with AI on real work. With a little more workflow design and review structure, the next band is within reach this quarter.
            </p>
            <div style={{ marginTop: 'auto', paddingTop: 22 }}>
              <div style={{ ...eyebrowStyle, letterSpacing: '0.12em', marginBottom: 10, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                Fluency velocity
                <InfoTip label="Fluency velocity" text={VELOCITY_DEF} />
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {(['low', 'medium', 'high'] as const).map((lvl) => (
                  <div
                    key={lvl}
                    style={{
                      flex: 1, textAlign: 'center', padding: '9px 0', borderRadius: 6, fontSize: 13, fontWeight: 700, textTransform: 'capitalize',
                      ...(lvl === r.velocity ? { background: '#00D700', color: '#000' } : { background: '#F4F4F6', color: '#ADAEB4' }),
                    }}
                  >
                    {lvl}
                  </div>
                ))}
              </div>
              <p style={{ fontSize: 12.5, lineHeight: 1.5, color: '#75757F', margin: '10px 0 0' }}>
                Steady, deliberate adoption — you keep the tools that prove their value.
              </p>
            </div>
          </section>
        </div>
        )}

        {/* 6. Bottleneck diagnosis */}
        <section className="animate-fadeUp" style={{ ...sectionStyle, animationDelay: '200ms' }}>
          <div style={{ ...eyebrowStyle, marginBottom: 6 }}>Bottleneck diagnosis</div>
          <h2 style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', margin: '0 0 22px' }}>Where your leverage leaks</h2>
          <div className="fui-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 }}>
            {r.primaryBottleneck && <BottleneckCard b={r.primaryBottleneck} tone="primary" />}
            {r.secondaryBottleneck && <BottleneckCard b={r.secondaryBottleneck} tone="secondary" />}
          </div>
          {r.primaryBottleneck && (r.primaryBottleneck.whatsOverProtected || r.primaryBottleneck.recommendedLane || r.lanePrescription) && (
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 18 }}>
              {r.primaryBottleneck.whatsOverProtected && (
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#75757F', marginBottom: 4 }}>What's over-protected</div>
                  <div style={{ fontSize: 14, lineHeight: 1.5, color: '#000' }}>{r.primaryBottleneck.whatsOverProtected}</div>
                </div>
              )}
              {(r.primaryBottleneck.recommendedLane || r.lanePrescription) && (
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#75757F', marginBottom: 4 }}>Recommended lane</div>
                  <div style={{ fontSize: 14, lineHeight: 1.5, color: '#000' }}>{r.primaryBottleneck.recommendedLane || r.lanePrescription}</div>
                </div>
              )}
            </div>
          )}
          {r.primaryBottleneck?.reframe && (
            <div style={{ borderLeft: '3px solid #00D700', background: '#F6FCF6', borderRadius: '0 8px 8px 0', padding: '16px 20px' }}>
              <div style={{ ...eyebrowStyle, letterSpacing: '0.12em', color: '#00A800', marginBottom: 6 }}>The reframe</div>
              <p style={{ fontSize: 16, lineHeight: 1.5, color: '#000', margin: 0, fontWeight: 500 }}>{r.primaryBottleneck.reframe}</p>
            </div>
          )}
        </section>

        {/* 7. Workflow findings + leverage snapshot — not captured for imports */}
        {!legacy && (r.workflowFindings.length > 0 || r.leverageSnapshot) && (
        <section className="animate-fadeUp" style={{ ...sectionStyle, animationDelay: '240ms' }}>
          <div style={{ ...eyebrowStyle, marginBottom: 6 }}>Dynamic workflow findings</div>
          <h2 style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', margin: '0 0 20px' }}>Where AI changes your role</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: r.leverageSnapshot ? 26 : 0 }}>
            {r.workflowFindings.map((w) => (
              <div key={w.name} style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '14px 16px', border: '1px solid #EFEFF2', borderRadius: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#00A800', flex: 'none', width: 96 }}>{w.name}</span>
                <span style={{ fontSize: 14, lineHeight: 1.5, color: '#333544' }}>
                  {w.observation} {w.opportunity}
                </span>
              </div>
            ))}
          </div>

          {r.leverageSnapshot && (
            <div style={{ background: '#000', color: '#fff', borderRadius: 12, padding: '26px 28px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 7, background: '#00D700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Zap size={14} color="#000" fill="#000" />
                  </div>
                  <span style={{ fontSize: 16, fontWeight: 700 }}>Leverage Snapshot</span>
                </div>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#ADAEB4', border: '1px solid #26262E', padding: '5px 11px', borderRadius: 999 }}>Safe to share with your manager</span>
              </div>
              <div className="fui-3col" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 1, background: '#26262E', border: '1px solid #26262E', borderRadius: 8, overflow: 'hidden' }}>
                <LeverageCell value={`~${r.leverageSnapshot.hoursRecoverable}h`} label="Weekly time recoverable from prep & drafting" green />
                <LeverageCell value={`${r.leverageSnapshot.workflowsToTemplatise}`} label="Workflow ready to templatise for the team" />
                <LeverageCell value={r.leverageSnapshot.targetQuarter} label={`Realistic timeframe to reach ${r.leverageSnapshot.targetBand.label} readiness`} />
              </div>
            </div>
          )}
        </section>
        )}

        {/* 8. Policy alignment */}
        <section className="animate-fadeUp" style={{ ...sectionStyle, animationDelay: '280ms' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
            <div>
              <div style={{ ...eyebrowStyle, marginBottom: 6 }}>Policy alignment &amp; approved-tool fit</div>
              <h2 style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', margin: 0 }}>Keeping it compliant</h2>
            </div>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, color: '#000', background: r.policyFit === 'compliant' ? '#E8F9E8' : '#FFFBEE', border: r.policyFit === 'compliant' ? '1px solid #B9E6B9' : '1px solid #E6D9B0', padding: '8px 14px', borderRadius: 8 }}>
              <span style={{ width: 9, height: 9, borderRadius: '50%', background: POLICY_SWATCH[r.policyFit] }} />
              {POLICY_LABEL[r.policyFit]}
            </span>
          </div>
          {r.policyViolations.length === 0 ? (
            <p style={{ fontSize: 14, lineHeight: 1.5, color: '#75757F', margin: 0 }}>
              No approved-tool issues were flagged in this assessment.
            </p>
          ) : (
          <div style={{ border: '1px solid #EFEFF2', borderRadius: 10, overflow: 'hidden' }}>
            <div className="fui-vhead" style={{ display: 'grid', gridTemplateColumns: '1.6fr 100px 1.4fr', background: '#FAFAFB', borderBottom: '1px solid #EFEFF2', padding: '11px 16px', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#75757F' }}>
              <div>Finding</div>
              <div>Severity</div>
              <div>Recommended action</div>
            </div>
            {r.policyViolations.map((v, i) => {
              const s = SEV[v.severity];
              return (
                <div key={i} className="fui-vrow" style={{ display: 'grid', gridTemplateColumns: '1.6fr 100px 1.4fr', padding: '14px 16px', borderBottom: i === r.policyViolations.length - 1 ? 'none' : '1px solid #EFEFF2', alignItems: 'start' }}>
                  <div style={{ paddingRight: 14 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#000' }}>{v.finding}</div>
                  </div>
                  <div>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 11px', borderRadius: 999, background: s.bg, color: s.ink }}>{s.label}</span>
                  </div>
                  <div style={{ fontSize: 13.5, lineHeight: 1.45, color: '#333544' }}>{v.recommendedAction}</div>
                </div>
              );
            })}
          </div>
          )}
        </section>

        {/* 8b. Organisation-specific dimensions — additive, org-authored, scored separately from
            the universal model. Skipped for legacy imports (which predate the feature). */}
        {!legacy && r.customDimensions && r.customDimensions.length > 0 && (
        <section className="animate-fadeUp" style={{ ...sectionStyle, animationDelay: '300ms' }}>
          <div style={{ ...eyebrowStyle, marginBottom: 6 }}>Set by your organisation</div>
          <h2 style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', margin: '0 0 6px' }}>Your organisation&rsquo;s expectations</h2>
          <p style={{ fontSize: 13.5, lineHeight: 1.5, color: '#75757F', margin: '0 0 6px' }}>
            Additional areas your organisation assesses, alongside the universal readiness model above.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {r.customDimensions.map((d, i) => (
              <CustomDimensionRow key={d.key} d={d} last={i === r.customDimensions!.length - 1} />
            ))}
          </div>
        </section>
        )}

        {/* 8.5 Recommended certification path — matched to the overall band so the participant's
            next step is spelled out for them. Frames the roadmap that follows. */}
        {!legacy && r.recommendedCertPath && (
          <section className="animate-fadeUp" style={{ ...sectionStyle, animationDelay: '300ms' }}>
            <div style={{ ...eyebrowStyle, marginBottom: 6 }}>Recommended next step</div>
            <h2 style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', margin: '0 0 18px' }}>Your certification path</h2>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 18, flexWrap: 'wrap' }}>
              <div style={{ width: 52, height: 52, borderRadius: 12, background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
                <Award size={26} color="#00D700" strokeWidth={1.8} />
              </div>
              <div style={{ flex: 1, minWidth: 260 }}>
                <div style={{ fontSize: 13, color: '#75757F', marginBottom: 4 }}>
                  Based on your <strong style={{ color: '#000' }}>{r.overallReadiness.label}</strong> readiness, we recommend:
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#000', margin: '0 0 6px' }}>{r.recommendedCertPath.name}</div>
                <p style={{ fontSize: 14.5, lineHeight: 1.55, color: '#333544', margin: 0, maxWidth: '60ch' }}>{r.recommendedCertPath.focus}</p>
                {r.recommendedCertPath.cert && (
                  <div style={{ marginTop: 16, border: '1px solid #E2E2E6', borderRadius: 10, padding: '14px 16px', background: '#FAFAFB', maxWidth: 460 }}>
                    <div style={{ ...eyebrowStyle, marginBottom: 6 }}>Industry certification to aim for</div>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                      <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#fff', background: '#000', borderRadius: 4, padding: '2px 7px' }}>{r.recommendedCertPath.cert.vendor}</span>
                      <span style={{ fontSize: 15, fontWeight: 700, color: '#000' }}>{r.recommendedCertPath.cert.name}</span>
                    </div>
                    <a
                      href={r.recommendedCertPath.cert.url}
                      target="_blank"
                      rel="noreferrer"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 8, fontSize: 13.5, fontWeight: 700, color: '#2438E6', textDecoration: 'none' }}
                    >
                      View certification <ArrowRight size={14} strokeWidth={2.4} />
                    </a>
                  </div>
                )}
                {r.recommendedCertPath.referenceUrl && (
                  <a
                    href={r.recommendedCertPath.referenceUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 12, fontSize: 13, fontWeight: 600, color: '#75757F', textDecoration: 'none' }}
                  >
                    Explore the learning roadmap <ArrowRight size={13} strokeWidth={2.2} />
                  </a>
                )}
              </div>
            </div>
          </section>
        )}

        {/* 9. 90-day roadmap — not captured for imports */}
        {!legacy && r.roadmap.length > 0 && (
        <section className="animate-fadeUp" style={{ ...sectionStyle, animationDelay: '320ms' }}>
          <div style={{ ...eyebrowStyle, marginBottom: 6 }}>90-day roadmap</div>
          <h2 style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', margin: '0 0 26px' }}>Your path forward</h2>
          <div className="fui-road" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 0, position: 'relative' }}>
            {r.roadmap.map((p, i) => {
              const current = i === 1;
              const dot = ['#2438E6', '#B52CF2', '#C2FF01', '#00D700'][i] ?? '#75757F';
              return (
                <div key={i} style={{ position: 'relative', padding: '0 16px 0 0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 16 }}>
                    <span style={{ width: 14, height: 14, borderRadius: '50%', background: dot, border: '3px solid #fff', boxShadow: current ? '0 0 0 1px #000' : '0 0 0 1px #E2E2E6', flex: 'none', zIndex: 2 }} />
                    <span style={{ flex: 1, height: 2, background: '#EFEFF2' }} />
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#75757F', letterSpacing: '0.04em' }}>
                    {p.window}
                    {current && <span style={{ marginLeft: 8, background: '#C2FF01', color: '#000', padding: '1px 7px', borderRadius: 999, fontSize: 10 }}>Now</span>}
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#000', margin: '5px 0 7px' }}>{p.title}</div>
                  <div style={{ fontSize: 13, lineHeight: 1.5, color: '#75757F' }}>{p.body}</div>
                </div>
              );
            })}
          </div>
        </section>
        )}

        {/* 10. Immediate 48-hour action */}
        {r.immediateAction && (
          <section className="animate-fadeUp" style={{ background: '#00D700', borderRadius: 12, padding: '30px 32px', display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={{ ...eyebrowStyle, color: '#0A4F0A', marginBottom: 8 }}>Your next 48 hours</div>
              <h2 style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', margin: '0 0 8px', color: '#000' }}>{r.immediateAction.title}</h2>
              <p style={{ fontSize: 15, lineHeight: 1.55, color: '#0A2A0A', margin: '0 0 16px', maxWidth: '60ch' }}>{r.immediateAction.body}</p>
              {!legacy && <CompletionControl itemType="action" itemRef="immediate-action" tone="onGreen" />}
            </div>
            <div style={{ width: 70, height: 70, borderRadius: 16, background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
              <Zap size={34} color="#00D700" strokeWidth={1.8} />
            </div>
          </section>
        )}

        {/* 11. Practice pack — generated content not captured for imports */}
        {!legacy && (
        <section className="animate-fadeUp" style={{ ...sectionStyle, animationDelay: '360ms' }}>
          <div style={{ ...eyebrowStyle, marginBottom: 6 }}>Personalised practice pack</div>
          <h2 style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', margin: '0 0 6px' }}>Keep building, in your own time</h2>
          <p style={{ fontSize: 14.5, lineHeight: 1.55, color: '#75757F', margin: '0 0 22px', maxWidth: '64ch' }}>
            Paste this master coach prompt into your approved AI workspace to run your own guided practice — or start the interactive drills below.
          </p>

          <MasterPrompt />

          {/* 3 drills */}
          <div className="fui-3col" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 24 }}>
            {r.practiceDrills.map((d, i) => (
              <div key={d.title} style={{ border: '1px solid #EFEFF2', borderRadius: 10, padding: 18, display: 'flex', flexDirection: 'column' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#00A800', marginBottom: 7 }}>DRILL {i + 1}</div>
                <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>{d.title}</div>
                <div style={{ fontSize: 11.5, color: '#75757F', borderTop: '1px solid #EFEFF2', paddingTop: 10, marginBottom: 12 }}>{d.rubricLine}</div>
                <div style={{ marginTop: 'auto' }}>
                  <CompletionControl itemType="drill" itemRef={String(i)} tone="light" />
                </div>
              </div>
            ))}
          </div>

          {/* options A–E + CTA */}
          <div className="fui-2col" style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 24, alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#75757F', marginBottom: 12 }}>After each drill, choose</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {PRACTICE_OPTIONS.map(([k, label]) => (
                  <div key={k} style={{ display: 'flex', gap: 11, alignItems: 'center' }}>
                    <span style={{ width: 22, height: 22, borderRadius: 5, background: '#000', color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>{k}</span>
                    <span style={{ fontSize: 14, color: '#333544' }}>{label}</span>
                  </div>
                ))}
              </div>
            </div>
            <StartPractice />
          </div>
        </section>
        )}
      </div>

      {drawer && <WhyDrawer criterion={drawer} onClose={() => setDrawer(null)} />}
    </div>
  );
}

function ProgressionChip({ p }: { p: NonNullable<ReportView['progression']> }) {
  const tone = p.direction === 'up' ? { ink: '#00D700', arrow: '↑' } : p.direction === 'down' ? { ink: '#FF5C5C', arrow: '↓' } : { ink: '#9A9AA4', arrow: '→' };
  const wks = Math.round(p.weeksElapsed);
  const label = p.direction === 'flat'
    ? `Held at ${p.currentBand.label} over ${wks} wk${wks === 1 ? '' : 's'}`
    : `${p.priorBand.label} ${tone.arrow} ${p.currentBand.label} over ${wks} wk${wks === 1 ? '' : 's'}`;
  return (
    <div style={{ marginTop: 8, fontSize: 11.5, fontWeight: 600, color: tone.ink, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <span>{label}</span>
      {p.priorImported && <span style={{ color: '#75757F', fontWeight: 500 }}>(summary prior)</span>}
    </div>
  );
}

function SummaryCell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ background: '#0B0B0F', padding: '16px 18px' }}>
      <div style={{ fontSize: 11, color: '#75757F', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ marginTop: 9 }}>{children}</div>
    </div>
  );
}

function SummaryStat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ fontSize: 13, color: '#75757F' }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 700, marginTop: 3 }}>{value}</div>
    </div>
  );
}

function LeverageCell({ value, label, green = false }: { value: string; label: string; green?: boolean }) {
  return (
    <div style={{ background: '#0B0B0F', padding: 18 }}>
      <div style={{ fontSize: 30, fontWeight: 800, letterSpacing: '-0.02em', color: green ? '#00D700' : '#fff', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      <div style={{ fontSize: 12.5, color: '#ADAEB4', marginTop: 6 }}>{label}</div>
    </div>
  );
}

function CriterionRow({ c, last, onWhy }: { c: CriterionView; last: boolean; onWhy: () => void }) {
  const pill = bandPill(c.band.band);
  return (
    <div style={{ padding: '18px 0', borderBottom: last ? 'none' : '1px solid #EFEFF2' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 16, fontWeight: 700 }}>
          {CRITERION_LABELS[c.criterion]}
          <InfoTip label={CRITERION_LABELS[c.criterion]} text={CRITERION_DEF[c.criterion]} />
        </span>
        <span style={{ fontSize: 12, fontWeight: 700, background: pill.background, color: pill.color, padding: '4px 11px', borderRadius: 999 }}>{c.band.label}</span>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 7 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#00A800', flex: 'none', width: 64 }}>STRENGTH</span>
        <span style={{ fontSize: 14, lineHeight: 1.5, color: '#333544' }}>{c.strength}</span>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#75757F', flex: 'none', width: 64 }}>NEXT STEP</span>
        <span style={{ fontSize: 14, lineHeight: 1.5, color: '#333544' }}>{c.nextStep}</span>
      </div>
      <button
        onClick={onWhy}
        style={{ marginTop: 11, background: 'transparent', border: 'none', padding: 0, fontSize: 12.5, fontWeight: 700, color: '#00A800', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
      >
        <HelpCircle size={14} /> Why this band?
      </button>
    </div>
  );
}

// An org-authored dimension row. Mirrors CriterionRow, but the label is org-defined (rendered
// from the payload, never a static map) and there is no "Why this band?" drawer — the rubric is
// org-private and not sent to the client. Informational-only dimensions show no band pill.
function CustomDimensionRow({ d, last }: { d: CustomDimensionView; last: boolean }) {
  const pill = bandPill(d.band.band);
  return (
    <div style={{ padding: '18px 0', borderBottom: last ? 'none' : '1px solid #EFEFF2' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9, fontSize: 16, fontWeight: 700 }}>
          {d.label}
          {d.informationalOnly && (
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#75757F', background: '#F0F0F2', padding: '2px 7px', borderRadius: 999 }}>Informational</span>
          )}
        </span>
        {!d.informationalOnly && (
          <span style={{ fontSize: 12, fontWeight: 700, background: pill.background, color: pill.color, padding: '4px 11px', borderRadius: 999 }}>{d.band.label}</span>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 7 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#00A800', flex: 'none', width: 64 }}>STRENGTH</span>
        <span style={{ fontSize: 14, lineHeight: 1.5, color: '#333544' }}>{d.strength}</span>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#75757F', flex: 'none', width: 64 }}>NEXT STEP</span>
        <span style={{ fontSize: 14, lineHeight: 1.5, color: '#333544' }}>{d.nextStep}</span>
      </div>
    </div>
  );
}

function BottleneckCard({ b, tone }: { b: { label: string; whatsOverProtected: string; recommendedLane: string }; tone: 'primary' | 'secondary' }) {
  return (
    <div style={{ border: '1px solid #EFEFF2', borderRadius: 10, padding: 18, background: '#FAFAFB' }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: tone === 'primary' ? '#E5142B' : '#9A7B00', marginBottom: 7 }}>
        {tone === 'primary' ? 'Primary' : 'Secondary'}
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color: '#000' }}>{b.label}</div>
      <p style={{ fontSize: 13.5, lineHeight: 1.5, color: '#75757F', margin: '7px 0 0' }}>{b.whatsOverProtected}</p>
    </div>
  );
}

function MasterPrompt() {
  const r = useReport();
  const copied = useStore((s) => s.copied);
  const copyPrompt = useStore((s) => s.copyPrompt);
  const onCopy = () => {
    navigator.clipboard?.writeText(r?.practiceMasterPrompt ?? '').catch(() => {});
    copyPrompt();
  };
  return (
    <div style={{ border: '1px solid #E2E2E6', borderRadius: 10, overflow: 'hidden', marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#0B0B0F', padding: '10px 14px' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#ADAEB4', fontFamily: 'monospace' }}>master-coach-prompt.txt</span>
        <button
          onClick={onCopy}
          style={{ fontSize: 12, fontWeight: 700, color: '#000', background: '#00D700', border: 'none', padding: '6px 13px', borderRadius: 5, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all .12s' }}
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <div style={{ background: '#fff', padding: '16px 18px', fontFamily: 'monospace', fontSize: 13, lineHeight: 1.65, color: '#333544', whiteSpace: 'pre-wrap' }}>
        {r?.practiceMasterPrompt}
      </div>
    </div>
  );
}

function StartPractice() {
  const setScreen = useStore((s) => s.setScreen);
  return (
    <button
      onClick={() => setScreen('practice')}
      style={{ background: '#000', color: '#fff', border: '2px solid #000', fontSize: 15, fontWeight: 700, padding: '16px 24px', borderRadius: 8, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10, transition: 'all .12s' }}
    >
      Start practice coach
      <ArrowRight size={18} strokeWidth={2.2} />
    </button>
  );
}

function WhyDrawer({ criterion, onClose }: { criterion: string; onClose: () => void }) {
  const r = useReport();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const c = r?.criteria.find((x) => x.criterion === criterion);
  if (!c) return null;
  const pill = bandPill(c.band.band);
  const confidenceLimit =
    c.confidence === 'high'
      ? 'We saw clear, consistent signals here — treat this band as well-supported.'
      : 'We saw fewer signals here, so treat this band as indicative. More examples would sharpen it.';

  return (
    <div className="fui-noprint print:hidden" style={{ position: 'fixed', inset: 0, zIndex: 80 }}>
      <div onClick={onClose} aria-hidden="true" style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)' }} />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={`Why ${CRITERION_LABELS[c.criterion]} is ${c.band.label}`}
        className="animate-slideIn"
        style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 440, maxWidth: '92vw', background: '#fff', boxShadow: '-18px 0 48px rgba(15,17,26,0.18)', display: 'flex', flexDirection: 'column' }}
      >
        <div style={{ padding: '24px 28px 20px', borderBottom: '1px solid #EFEFF2' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
            <span style={{ ...eyebrowStyle }}>Why this band</span>
            <button
              onClick={onClose}
              aria-label="Close"
              style={{ width: 32, height: 32, borderRadius: 7, border: '1px solid #E2E2E6', background: '#fff', color: '#333544', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .12s' }}
            >
              <X size={17} />
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 11, flexWrap: 'wrap' }}>
            <h3 style={{ fontSize: 21, fontWeight: 700, letterSpacing: '-0.02em', margin: 0 }}>{CRITERION_LABELS[c.criterion]}</h3>
            <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 11px', borderRadius: 999, background: pill.background, color: pill.color }}>{c.band.label}</span>
          </div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, marginTop: 10, fontSize: 12.5, fontWeight: 600, color: c.confidence === 'low' ? '#9A7B00' : '#333544', textTransform: 'capitalize' }}>
            <Info size={14} /> {c.confidence} confidence in this rating
          </div>
        </div>

        <div className="fui-scroll" style={{ flex: 1, overflowY: 'auto', padding: '22px 28px 28px' }}>
          <div style={{ ...eyebrowStyle, letterSpacing: '0.12em', marginBottom: 13 }}>Evidence from your conversation</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
            {c.evidenceQuotes.map((q, i) => (
              <div key={i} style={{ display: 'flex', gap: 11, alignItems: 'flex-start', borderLeft: '2px solid #00D700', background: '#F6FCF6', borderRadius: '0 8px 8px 0', padding: '12px 14px' }}>
                <span style={{ fontSize: 13.5, lineHeight: 1.5, color: '#333544' }}>{q}</span>
              </div>
            ))}
          </div>

          <div style={{ background: '#FFFBEE', border: '1px solid #E6D9B0', borderRadius: 9, padding: '14px 16px', marginBottom: 22 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#9A7B00', marginBottom: 6 }}>How confident we are</div>
            <div style={{ fontSize: 13.5, lineHeight: 1.55, color: '#333544' }}>{confidenceLimit}</div>
          </div>

          <div style={{ borderTop: '1px solid #EFEFF2', paddingTop: 18 }}>
            <div style={{ ...eyebrowStyle, letterSpacing: '0.12em', marginBottom: 7 }}>Most useful next step</div>
            <div style={{ fontSize: 14.5, lineHeight: 1.55, color: '#000', fontWeight: 500 }}>{c.nextStep}</div>
          </div>

        </div>
      </aside>
    </div>
  );
}

/**
 * Tick off a practice drill or the 48-hour action, with an optional short reflection.
 * Reads/writes the store's persisted completions. `tone` adapts the colours to the card it
 * sits on (the green 48h banner vs a white drill card).
 */
function CompletionControl({
  itemType,
  itemRef,
  tone = 'light',
}: {
  itemType: 'drill' | 'action';
  itemRef: string;
  tone?: 'light' | 'onGreen';
}) {
  const completions = useStore((s) => s.completions);
  const markComplete = useStore((s) => s.markComplete);
  const existing = completions.find((c) => c.itemType === itemType && c.itemRef === itemRef && c.done);
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState('');

  useEffect(() => {
    setNote(existing?.reflection ?? '');
  }, [existing?.reflection]);

  const onGreen = tone === 'onGreen';
  const accent = onGreen ? '#000' : '#00A800';
  const subtle = onGreen ? '#0A4F0A' : '#75757F';

  const save = () => {
    markComplete(itemType, itemRef, note);
    setEditing(false);
  };

  if (existing && !editing) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: accent }}>
          <Check size={15} strokeWidth={3} /> Done
        </span>
        {existing.reflection && (
          <p style={{ fontSize: 12.5, lineHeight: 1.5, color: subtle, margin: 0, fontStyle: 'italic' }}>“{existing.reflection}”</p>
        )}
        <button
          type="button"
          onClick={() => setEditing(true)}
          style={{ alignSelf: 'flex-start', background: 'none', border: 'none', padding: 0, fontSize: 12, fontWeight: 600, color: subtle, textDecoration: 'underline', cursor: 'pointer' }}
        >
          {existing.reflection ? 'Edit note' : 'Add a note'}
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Optional: a quick reflection — what worked, what you'd change"
        rows={2}
        style={{
          width: '100%',
          border: `1px solid ${onGreen ? '#0A4F0A' : '#E2E2E6'}`,
          borderRadius: 8,
          padding: '8px 10px',
          fontFamily: 'inherit',
          fontSize: 13,
          lineHeight: 1.5,
          color: '#000',
          background: onGreen ? 'rgba(255,255,255,0.85)' : '#fff',
          resize: 'vertical',
          outline: 'none',
          boxSizing: 'border-box',
        }}
      />
      <button
        type="button"
        onClick={save}
        style={{
          alignSelf: 'flex-start',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          background: onGreen ? '#000' : '#00D700',
          color: onGreen ? '#00D700' : '#000',
          border: 'none',
          borderRadius: 6,
          padding: '8px 14px',
          fontSize: 13,
          fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        <Check size={14} strokeWidth={3} /> {existing ? 'Save note' : 'Mark as done'}
      </button>
    </div>
  );
}
